import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import { useTheme } from "next-themes";
import { Toaster as Sonner, toast } from "sonner";
import { useLarguraSm } from "@/hooks/useLarguraSm";
import { textoDoAviso, type TipoAviso } from "@/lib/textoDoAviso";

type ToasterProps = React.ComponentProps<typeof Sonner>;

type DadosDoAviso = Parameters<typeof toast.error>[1];

const TIPOS_PROTEGIDOS: readonly TipoAviso[] = [
  "success",
  "error",
  "info",
  "warning",
  "loading",
  "message",
];

/** O erro fica na tela 8 s, como o aviso crítico do sino; os outros tipos valem os 5 s do Toaster. */
const DURACAO_DO_ERRO_MS = 8000;

// `Symbol.for` (registro global): vale mesmo se este módulo for avaliado de novo (HMR do Vite).
const MARCA_PROTEGIDO = Symbol.for("seweb.avisos.sonner.protegido");

/**
 * Embrulha `toast.success/error/info/warning/loading/message` para o título e a descrição
 * chegarem ao sonner SEMPRE como texto. O sonner renderiza o título cru; um `detail` de 422
 * (lista de objetos) ou um objeto de erro derrubaria a árvore do React ("Objects are not
 * valid as a React child"). Também troca o jargão do axios por português (textoDoAviso).
 *
 * No `error`, entrega também `duration: 8000` (quem passa `duration` próprio continua
 * mandando). Idempotente: marca o objeto para não embrulhar duas vezes. Devolve o retorno do
 * original (o `loading` devolve o id, usado depois em `toast.dismiss(id)`).
 */
// eslint-disable-next-line react-refresh/only-export-components -- função de módulo, chamada uma vez abaixo; sem componente para o Fast Refresh preservar
export function protegerAvisosDoSonner(alvo: typeof toast = toast): void {
  const marcado = alvo as typeof alvo & { [MARCA_PROTEGIDO]?: true };
  if (marcado[MARCA_PROTEGIDO]) return;

  for (const tipo of TIPOS_PROTEGIDOS) {
    const original = alvo[tipo];
    // Mock de teste sem o método: não há o que embrulhar.
    if (typeof original !== "function") continue;
    alvo[tipo] = (mensagem, dados?: DadosDoAviso) => {
      const protegidos =
        dados && "description" in dados
          ? { ...dados, description: textoDoAviso(dados.description, { tipo, obrigatorio: false }) }
          : dados;
      return original(
        textoDoAviso(mensagem, { tipo, obrigatorio: true }),
        tipo === "error"
          ? { ...protegidos, duration: protegidos?.duration ?? DURACAO_DO_ERRO_MS }
          : protegidos
      );
    };
  }
  marcado[MARCA_PROTEGIDO] = true;
}

// O `toast` do sonner é UM objeto compartilhado por todos os `import { toast } from "sonner"`
// do app (131 arquivos, 544 chamadas). Embrulhar aqui, no módulo que monta o Toaster, protege
// todas sem tocar em nenhuma. O `toast(...)` puro (a função em si) não tem uso no app, por
// isso não é embrulhado; `toast.promise` e `toast.custom` também não têm uso.
protegerAvisosDoSonner();

const ABAIXO_DO_CABECALHO = "calc(env(safe-area-inset-top, 0px) + 64px)";

// Ícones do lucide, 16 px: o `error` é o mesmo do aviso crítico do sino (AvisoNaTela).
const ICONES = {
  success: <CheckCircle2 className="h-4 w-4" />,
  info: <Info className="h-4 w-4" />,
  warning: <AlertTriangle className="h-4 w-4" />,
  error: <AlertCircle className="h-4 w-4" />,
  close: <X className="h-4 w-4" />,
};

// Os `!` são de propósito: o sonner junta as classes sem `twMerge`, e no CSS gerado do projeto
// `.bg-background` sai depois de `.bg-green-50`; o botão de fechar e os botões do sonner têm
// seletores de especificidade (0,2,0)/(0,3,0). Sem o `!`, a cor do tipo perderia para o fundo
// base. A base do sonner 1.7.4 é `:where(...)` (especificidade 0), então o resto não precisa.
// O título passa de 8,7:1 de contraste em todos os tipos, no claro e no escuro; a descrição não
// usa `text-muted-foreground` (4,33:1 no claro).
const CLASSES_DOS_AVISOS = {
  toast:
    "group toast items-start gap-3 rounded-md border border-border bg-background p-6 pr-8 font-body text-sm text-foreground shadow-lg",
  title: "font-semibold leading-5",
  description: "leading-5 opacity-90",
  content: "min-w-0 gap-1",
  icon: "mx-0 mt-0.5 h-4 w-4 shrink-0",
  loader: "[&_.sonner-loading-bar]:bg-muted-foreground",
  closeButton:
    "left-auto right-2 top-2 h-auto w-auto translate-x-0 translate-y-0 rounded-md !border-0 !bg-transparent p-1 !text-foreground/50 opacity-100 transition-opacity hover:!text-foreground focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring sm:opacity-0 group-hover:opacity-100",
  actionButton:
    "!h-8 !rounded-md !border !border-border !bg-transparent !px-3 !text-sm font-medium !text-foreground transition-colors hover:!bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
  cancelButton:
    "!h-8 !rounded-md !border-0 !bg-transparent !px-3 !text-sm font-medium !text-muted-foreground hover:!bg-muted hover:!text-foreground",
  success:
    "!border-green-500 !bg-green-50 !text-green-900 dark:!border-green-800 dark:!bg-green-950 dark:!text-green-100",
  error:
    "!border-red-600 !bg-red-50 !text-red-900 dark:!border-red-800 dark:!bg-red-950 dark:!text-red-100",
  warning:
    "!border-amber-500 !bg-amber-50 !text-amber-900 dark:!border-amber-700 dark:!bg-amber-950 dark:!text-amber-100",
  info: "!border-blue-500 !bg-blue-50 !text-blue-900 dark:!border-blue-800 dark:!bg-blue-950 dark:!text-blue-100",
};

const Toaster = ({ ...props }: ToasterProps) => {
  const { resolvedTheme } = useTheme();
  const largo = useLarguraSm();

  return (
    // O `pointerdown` no aviso para aqui: o Dialog do Radix fecha no `pointerdown` que chega ao
    // document vindo de fora dele, e fechar o aviso pelo X fechava junto o diálogo aberto (e o
    // formulário). O aviso do shadcn é uma camada do Radix e não tem esse problema.
    // `display: contents`: o embrulho não entra no layout.
    <div style={{ display: "contents" }} onPointerDown={(evento) => evento.stopPropagation()}>
      <Sonner
        className="toaster group"
        theme={resolvedTheme === "dark" ? "dark" : "light"}
        // Mesmo corte do aviso do shadcn: embaixo à direita no computador; no celular, no topo,
        // logo abaixo do cabeçalho de 64 px (sem cobrir o Menu e o sino).
        position={largo ? "bottom-right" : "top-center"}
        offset={largo ? 16 : { top: ABAIXO_DO_CABECALHO }}
        mobileOffset={{ top: ABAIXO_DO_CABECALHO, bottom: 16, left: 16, right: 16 }}
        // Cor só pelo tipo (nas classes abaixo), não pelo `richColors` do sonner.
        richColors={false}
        closeButton
        // 5 s: o padrão do Radix, que o shadcn já usa. O erro dura 8 s (protegerAvisosDoSonner).
        duration={5000}
        visibleToasts={3}
        expand={false}
        gap={12}
        icons={ICONES}
        // O viewport do shadcn é z-[100]: o aviso do sino fica por cima quando os dois aparecem
        // juntos. Os diálogos são z-50. Inline vence o `z-index: 999999999` do CSS do sonner.
        // 388 px = os 420 do viewport do shadcn menos 2 × 16 de folga.
        // `pointerEvents: auto`: com um diálogo do Radix aberto o body fica `pointer-events:none`, e o
        // aviso herdava isso (o clique atravessava para o fundo do diálogo e o X não respondia). O
        // aviso do shadcn faz o mesmo com `pointer-events-auto`.
        style={{ zIndex: 99, pointerEvents: "auto", "--width": "388px" } as React.CSSProperties}
        toastOptions={{ classNames: CLASSES_DOS_AVISOS }}
        // Nome da região para o leitor de tela (o padrão do sonner é "Notifications"); o shadcn usa "Avisos".
        containerAriaLabel="Avisos"
        {...props}
      />
    </div>
  );
};

export { Toaster };
